import { useState, useEffect, useRef, useCallback } from "react";
import { auth, loginWithGoogle, saveScore, fetchLeaderboard } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function formatTime(ms) {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor((ms % 1000) / 10);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(2, "0")}`;
}

const BOX_COLORS = [
  "#FF6B6B","#FF8E53","#FFCC02","#A8E063","#56CCF2",
  "#6C63FF","#F78FB3","#43E97B","#FA8231","#4FACFE",
  "#F093FB","#4ECDC4","#FFD93D","#C4B5FD","#FCA5A5",
  "#6EE7B7","#FDE68A","#BAE6FD","#DDD6FE","#FBCFE8",
  "#86EFAC","#FCA5A5","#93C5FD","#FCD34D","#A5F3FC",
  "#FB923C","#818CF8","#34D399","#F472B6","#60A5FA",
];

const TEXT_COLORS = [
  "#1a1a2e","#ffffff","#1a1a2e","#1a1a2e","#1a1a2e",
  "#ffffff","#1a1a2e","#1a1a2e","#ffffff","#1a1a2e",
  "#ffffff","#1a1a2e","#1a1a2e","#1a1a2e","#1a1a2e",
  "#1a1a2e","#1a1a2e","#1a1a2e","#1a1a2e","#1a1a2e",
  "#1a1a2e","#1a1a2e","#1a1a2e","#1a1a2e","#1a1a2e",
  "#ffffff","#ffffff","#1a1a2e","#1a1a2e","#1a1a2e",
];

// Sound Engine
let _audioCtx = null;

function getCtx() {
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_audioCtx.state === "suspended") {
    _audioCtx.resume();
  }
  return _audioCtx;
}

function playCorrect(step) {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const freq = 520 + step * 8;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.3, ctx.currentTime + 0.08);
    osc.type = "sine";
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) { console.warn("playCorrect error:", e); }
}

function playMistake() {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + 0.18);
    osc.type = "sawtooth";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } catch (e) { console.warn("playMistake error:", e); }
}

function playComplete() {
  try {
    const ctx = getCtx();
    [523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      const t = ctx.currentTime + i * 0.13;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.22, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.start(t);
      osc.stop(t + 0.4);
    });
  } catch (e) { console.warn("playComplete error:", e); }
}

// Confetti
const EMOJIS = ["🌸","🌺","🌻","🌼","💐","✨","🎉","⭐","🎊","🌟","💫","🏆"];
function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    particlesRef.current = Array.from({ length: 70 }, () => ({
      x: Math.random() * canvas.width,
      y: -40 - Math.random() * 200,
      emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
      size: 18 + Math.random() * 24,
      speedX: (Math.random() - 0.5) * 3,
      speedY: 2.5 + Math.random() * 3,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 6,
      opacity: 1,
    }));

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        p.x += p.speedX; p.y += p.speedY; p.rotation += p.rotSpeed;
        if (p.y > canvas.height * 0.65) p.opacity -= 0.018;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.font = `${p.size}px serif`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(p.emoji, 0, 0);
        ctx.restore();
      });
      particlesRef.current = particlesRef.current.filter(p => p.opacity > 0);
      if (particlesRef.current.length > 0) rafRef.current = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  if (!active) return null;
  return (
    <canvas ref={canvasRef} style={{
      position: "fixed", top: 0, left: 0,
      width: "100vw", height: "100vh",
      pointerEvents: "none", zIndex: 999,
    }} />
  );
}

// Splash
function SplashScreen({ onLogin, onGuest, loading }) {
  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "24px", fontFamily: "'Segoe UI', system-ui, sans-serif"
    }}>
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{
          width: 96, height: 96, borderRadius: 24, background: "linear-gradient(135deg, #f093fb, #f5576c)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 44, margin: "0 auto 20px", boxShadow: "0 8px 32px rgba(240,147,251,0.4)"
        }}>🧠</div>
        <h1 style={{ color: "#ffffff", fontSize: 36, fontWeight: 900, margin: 0, letterSpacing: -1 }}>
          Focus<span style={{ color: "#f093fb" }}>Grid</span>
        </h1>
        <p style={{ color: "rgba(255,255,255,0.6)", margin: "8px 0 0", fontSize: 15 }}>
          1 → 30 · As fast as you can
        </p>
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, 44px)", gap: 4,
        marginBottom: 40, padding: 12,
        background: "rgba(255,255,255,0.05)", borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.1)"
      }}>
        {shuffle([...Array(30).keys()].map(i => i + 1)).slice(0, 10).map((n, i) => (
          <div key={i} style={{
            width: 44, height: 44, borderRadius: 10,
            background: BOX_COLORS[n - 1], display: "flex",
            alignItems: "center", justifyContent: "center",
            fontWeight: 900, fontSize: 16, color: TEXT_COLORS[n - 1]
          }}>{n}</div>
        ))}
      </div>
      <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={onLogin} disabled={loading} style={{
          padding: "16px 24px", borderRadius: 16, border: "none", cursor: loading ? "not-allowed" : "pointer",
          background: loading ? "rgba(255,255,255,0.5)" : "#ffffff",
          color: "#1a1a2e", fontSize: 16, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}>
          <span style={{ fontSize: 20 }}>G</span>
          {loading ? "Signing in…" : "Sign in with Google"}
        </button>
        <button onClick={onGuest} disabled={loading} style={{
          padding: "14px 24px", borderRadius: 16, cursor: "pointer",
          background: "transparent", color: "rgba(255,255,255,0.5)",
          fontSize: 15, fontWeight: 600, border: "1px solid rgba(255,255,255,0.2)"
        }}>Play as Guest (local only)</button>
      </div>
    </div>
  );
}

// Game
function GameScreen({ user, onFinish, onLeaderboard }) {
  const [numbers] = useState(() => {
    const nums = shuffle([...Array(30).keys()].map(i => i + 1));
    const colorIndices = shuffle([...Array(30).keys()]);
    return nums.map((n, i) => ({ value: n, colorIdx: colorIndices[i] }));
  });
  const [next, setNext] = useState(1);
  const [tapped, setTapped] = useState(new Set());
  const [mistakes, setMistakes] = useState(0);
  const [steps, setSteps] = useState(0);
  const [flash, setFlash] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => setElapsed(Date.now() - startTime), 33);
    }
    return () => clearInterval(timerRef.current);
  }, [running, startTime]);

  const handleTap = useCallback((idx, value) => {
    if (tapped.has(idx) || done) return;
    if (!running) { setStartTime(Date.now()); setRunning(true); }
    setSteps(s => s + 1);
    if (value === next) {
      playCorrect(next);
      setFlash({ idx, type: "correct" });
      setTimeout(() => setFlash({}), 300);
      const newTapped = new Set(tapped);
      newTapped.add(idx);
      setTapped(newTapped);
      const newNext = next + 1;
      setNext(newNext);
      if (newNext > 30) {
        clearInterval(timerRef.current);
        setRunning(false); setDone(true);
        const finalTime = Date.now() - startTime;
        setElapsed(finalTime);
        playComplete();
        setShowConfetti(true);
        setTimeout(() => onFinish({ time: finalTime, mistakes, steps: steps + 1 }), 1800);
      }
    } else {
      playMistake();
      setFlash({ idx, type: "wrong" });
      setTimeout(() => setFlash({}), 400);
      setMistakes(m => m + 1);
    }
  }, [tapped, done, running, next, startTime, mistakes, steps, onFinish]);

  return (
    <>
      <ConfettiCanvas active={showConfetti} />
      <div style={{
        minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
        display: "flex", flexDirection: "column", alignItems: "center",
        fontFamily: "'Segoe UI', system-ui, sans-serif", padding: "80px 12px 16px"
      }}>
        <div style={{ width: "100%", maxWidth: 420, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "linear-gradient(135deg, #f093fb, #f5576c)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: 13
            }}>{user?.avatar || "G"}</div>
            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}>{user?.displayName || user?.name || "Guest"}</span>
          </div>
          <button onClick={onLeaderboard} style={{
            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 12, padding: "6px 14px", color: "#fff", cursor: "pointer", fontSize: 13
          }}>🏆 Ranks</button>
        </div>

        <div style={{ width: "100%", maxWidth: 420, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          {[
            { label: "TIME", value: formatTime(elapsed), accent: "#f093fb" },
            { label: "STEPS", value: steps, accent: "#43e97b" },
            { label: "MISTAKES", value: mistakes, accent: mistakes > 0 ? "#ff6b6b" : "#43e97b" },
          ].map(({ label, value, accent }) => (
            <div key={label} style={{
              background: "rgba(255,255,255,0.07)", borderRadius: 14,
              padding: "10px 8px", textAlign: "center",
              border: "1px solid rgba(255,255,255,0.1)"
            }}>
              <div style={{ color: accent, fontSize: 18, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>{value}</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 10, color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
          {done ? "🎉 Complete!" : <>Find: <span style={{ color: "#f093fb", fontWeight: 900, fontSize: 20 }}>{next}</span></>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6, width: "100%", maxWidth: 420 }}>
          {numbers.map(({ value, colorIdx }, idx) => {
            const isTapped = tapped.has(idx);
            const isFlash = flash.idx === idx;
            let bg = BOX_COLORS[colorIdx];
            let textCol = TEXT_COLORS[colorIdx];
            if (isTapped) { bg = "rgba(255,255,255,0.08)"; textCol = "rgba(255,255,255,0.2)"; }
            if (isFlash && flash.type === "correct") bg = "#43e97b";
            if (isFlash && flash.type === "wrong") bg = "#ff4444";
            return (
              <div key={idx} onClick={() => handleTap(idx, value)} style={{
                aspectRatio: "1", borderRadius: 12, background: bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 900, fontSize: 20, color: isTapped ? "rgba(255,255,255,0.15)" : textCol,
                cursor: isTapped ? "default" : "pointer",
                transition: "all 0.15s ease",
                transform: isFlash ? "scale(0.88)" : "scale(1)",
                boxShadow: isTapped ? "none" : "0 2px 8px rgba(0,0,0,0.3)",
                userSelect: "none", WebkitUserSelect: "none",
              }}>
                {isTapped ? "✓" : value}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// Result
function ResultScreen({ user, result, onReplay, onLeaderboard, bestTime, saving }) {
  const isPB = !bestTime || result.time <= bestTime;
  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "'Segoe UI', system-ui, sans-serif"
    }}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>{result.mistakes === 0 ? "🏆" : "🎯"}</div>
      <h2 style={{ color: "#fff", fontSize: 28, fontWeight: 900, margin: "0 0 4px" }}>
        {result.mistakes === 0 ? "Perfect!" : "Finished!"}
      </h2>
      {isPB && <div style={{ color: "#FFD700", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>🌟 New Personal Best!</div>}
      {saving && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginBottom: 8 }}>Saving to leaderboard…</div>}
      <div style={{
        background: "rgba(255,255,255,0.07)", borderRadius: 20, padding: 24,
        width: "100%", maxWidth: 320, border: "1px solid rgba(255,255,255,0.12)", marginBottom: 24
      }}>
        {[
          { label: "Time", value: formatTime(result.time), big: true },
          { label: "Total Steps", value: result.steps },
          { label: "Mistakes", value: result.mistakes },
        ].map(({ label, value, big }) => (
          <div key={label} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.07)"
          }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>{label}</span>
            <span style={{ color: "#fff", fontSize: big ? 28 : 18, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>{value}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, width: "100%", maxWidth: 320 }}>
        <button onClick={onReplay} style={{
          flex: 1, padding: "16px", borderRadius: 16, border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #f093fb, #f5576c)", color: "#fff", fontSize: 16, fontWeight: 700
        }}>Play Again</button>
        <button onClick={onLeaderboard} style={{
          flex: 1, padding: "16px", borderRadius: 16, cursor: "pointer",
          background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff", fontSize: 16, fontWeight: 700
        }}>🏆 Ranks</button>
      </div>
    </div>
  );
}

// Leaderboard
function LeaderboardScreen({ firebaseUser, myResult, onBack }) {
  const [leaders, setLeaders] = useState([]);
  const [loadingLB, setLoadingLB] = useState(true);
  useEffect(() => {
    fetchLeaderboard().then(setLeaders).catch(console.error).finally(() => setLoadingLB(false));
  }, []);
  const myRank = firebaseUser && leaders.findIndex(l => l.uid === firebaseUser.uid) + 1 || null;
  const medalEmoji = (r) => r === 1 ? "🥇" : r === 2 ? "🥈" : r === 3 ? "🥉" : null;
  const medalBg = (r) => r === 1 ? "linear-gradient(135deg,#FFD700,#FFA500)" : r === 2 ? "linear-gradient(135deg,#C0C0C0,#A0A0A0)" : r === 3 ? "linear-gradient(135deg,#CD7F32,#8B4513)" : null;

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
      display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', system-ui, sans-serif"
    }}>
      <div style={{
        padding: "16px 20px", display: "flex", alignItems: "center", gap: 12,
        borderBottom: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.2)"
      }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#f093fb", fontSize: 22, cursor: "pointer", padding: 4 }}>←</button>
        <h2 style={{ color: "#fff", margin: 0, fontSize: 20, fontWeight: 900 }}>🏆 World Rankings</h2>
      </div>
      {loadingLB ? (
        <div style={{ color: "rgba(255,255,255,0.5)", textAlign: "center", marginTop: 60, fontSize: 16 }}>Loading…</div>
      ) : leaders.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", marginTop: 60, fontSize: 15 }}>No scores yet. Be the first! 🚀</div>
      ) : (
        <>
          {leaders.length >= 3 && (
            <div style={{ padding: "24px 16px 16px", display: "flex", justifyContent: "center", gap: 12 }}>
              {[leaders[1], leaders[0], leaders[2]].map((p, pIdx) => {
                if (!p) return null;
                const rank = pIdx === 1 ? 1 : pIdx === 0 ? 2 : 3;
                const heights = [100, 128, 86];
                return (
                  <div key={p.uid} style={{ textAlign: "center", width: 100 }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: "50%", margin: "0 auto 6px",
                      background: medalBg(rank) || "rgba(255,255,255,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20, fontWeight: 900, color: "#fff",
                      boxShadow: rank === 1 ? "0 0 20px rgba(255,215,0,0.5)" : "none",
                      border: rank === 1 ? "2px solid #FFD700" : "none"
                    }}>{p.avatar}</div>
                    <div style={{ color: "#fff", fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{p.name?.split(" ")[0]}</div>
                    <div style={{
                      height: heights[pIdx], background: medalBg(rank) || "rgba(255,255,255,0.1)",
                      borderRadius: "8px 8px 0 0", display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "flex-start", paddingTop: 8
                    }}>
                      <div style={{ fontSize: 22 }}>{medalEmoji(rank)}</div>
                      <div style={{ color: "#fff", fontSize: 12, fontWeight: 900, marginTop: 4 }}>{formatTime(p.time)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 80px" }}>
            {leaders.map((player, i) => {
              const rank = i + 1;
              const isTop3 = rank <= 3;
              const isMe = firebaseUser && player.uid === firebaseUser.uid;
              return (
                <div key={player.uid} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", marginBottom: 6, borderRadius: 14,
                  background: isMe ? "rgba(240,147,251,0.15)" : "rgba(255,255,255,0.05)",
                  border: isMe ? "1px solid rgba(240,147,251,0.4)" : "1px solid rgba(255,255,255,0.06)"
                }}>
                  <div style={{ width: 28, textAlign: "center", fontWeight: 900, fontSize: 14, color: isTop3 ? "#FFD700" : "rgba(255,255,255,0.4)" }}>
                    {isTop3 ? medalEmoji(rank) : rank}
                  </div>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg, #f093fb, #f5576c)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 900, color: "#fff"
                  }}>{player.avatar}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: isMe ? "#f093fb" : "#fff", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {player.name} {isMe ? "(You)" : ""}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
                      {player.mistakes === 0 ? "✨ Perfect" : `${player.mistakes} mistake${player.mistakes > 1 ? "s" : ""}`}
                    </div>
                  </div>
                  <div style={{ color: "#43e97b", fontWeight: 900, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                    {formatTime(player.time)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {myRank > 0 && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(135deg,rgba(240,147,251,0.9),rgba(245,87,108,0.9))",
          padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
          backdropFilter: "blur(10px)"
        }}>
          <span style={{ color: "#fff", fontWeight: 700 }}>Your World Rank</span>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 22 }}>#{myRank}</span>
        </div>
      )}
    </div>
  );
}

// Main App
export default function App() {
  const [screen, setScreen] = useState("splash");
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [bestTime, setBestTime] = useState(null);
  const [gameKey, setGameKey] = useState(0);
  const [authLoading, setAuthLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) { setFirebaseUser(user); setGuestMode(false); setScreen("game"); }
    });
    return unsub;
  }, []);

  const handleLogin = async () => {
    setAuthLoading(true);
    try { await loginWithGoogle(); }
    catch (err) { alert("Login failed: " + err.message); }
    finally { setAuthLoading(false); }
  };

  const handleGuest = () => { setGuestMode(true); setFirebaseUser(null); setScreen("game"); };

  const handleFinish = async (result) => {
    setLastResult(result);
    const isNewBest = !bestTime || result.time < bestTime;
    if (isNewBest) setBestTime(result.time);
    setScreen("result");
    if (firebaseUser && isNewBest) {
      setSaving(true);
      try { await saveScore(firebaseUser, result); }
      catch (err) { console.error("Save failed", err); }
      finally { setSaving(false); }
    }
  };

  const handleReplay = () => { setGameKey(k => k + 1); setScreen("game"); };

  const displayUser = firebaseUser
    ? { ...firebaseUser, avatar: firebaseUser.displayName?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "U" }
    : guestMode ? { name: "Guest", avatar: "G" } : null;

  if (screen === "splash") return <SplashScreen onLogin={handleLogin} onGuest={handleGuest} loading={authLoading} />;
  if (screen === "game") return <GameScreen key={gameKey} user={displayUser} onFinish={handleFinish} onLeaderboard={() => setScreen("leaderboard")} />;
  if (screen === "result") return <ResultScreen user={displayUser} result={lastResult} bestTime={bestTime} saving={saving} onReplay={handleReplay} onLeaderboard={() => setScreen("leaderboard")} />;
  if (screen === "leaderboard") return <LeaderboardScreen firebaseUser={firebaseUser} myResult={lastResult} onBack={() => setScreen(lastResult ? "result" : "game")} />;
}